import * as uuid from 'uuid';
import {
	getRehabTrees,
	getRehabTreesResolved,
	getRehabTreesRejected,
	getRehabTreeResolved,
	getRehabTreeRejected,
	getRehabTree,
	addRehabTreeRejected,
	addRehabTree,
	addRehabTreeResolved,
	saveRehabTree,
	deleteRehabTree,
	deleteRehabTreeResolved,
	deleteRehabTreeRejected,
	getRehabActionsResolved,
	getRehabActionsRejected,
	getRehabActions,
	validateRehabTree,
	validateRehabTreeRejected,
	runRehabTreeRejected,
	runRehabTree,
	setRehabResultIsBusy,
	getRehabResultResolved,
	getRehabResultRejected,
	getRehabResult,
	reloadRehabResult,
	changeFilterSelect,
	changeSort,
	changePage,
	updateColumnFilterSelectOptions,
	addRehabTreeValidationResult,
	duplicateRehabTreeRejected,
	duplicateRehabTree,
	setStartNodeId,
	getRehabCostResolved,
	getRehabCostRejected,
	getRehabCost,
	setRehabTreesIsBusy,
	getRehabTreesListResolved,
	getRehabTreesListRejected,
	getRehabTreesList,
	reloadRehabTrees,
	changeRehabTreesPage,
	changeRehabTreesSlice,
	changeRehabTreesSort,
	duplicateRehabTreeResolved,
	setRefreshList,
	renameRehabTree,
	setRehabTreesIsRenaming,
	setRehabTreeNameError,
	getRehabTreeStatusRejected,
	getRehabTreeStatusResolved,
	getRehabTreeStatus,
	getRehabTreeValidationStatus,
	getRehabTreeValidationStatusResolved,
	getRehabTreeValidationStatusRejected,
	changePagination,
	changeFiltersSelect,
	getAssetFilters,
	getAssetFiltersRejected,
	getAssetFiltersResolved,
	addRehabTreeCreated,
	clearScanId,
	getGroupResult,
	reloadGroupResult,
	getGroupResultResolved,
	setInitalLoadSet,
	setIsDirty,
	setShowReadyToUse,
	setShowWarning,
	setRehabTreePublishState,
} from '@Actions/Rehab.actions';
import {
	put,
	retry,
	takeLatest,
	select,
	call,
	all,
} from '@redux-saga/core/effects';

import { addRouteNamespace } from '@Utils/actions';

import {
	type ReactFlowDecisionTree,
	type ReactFlowSchema,
	type RehabTreeResolvedResponse,
	type NewDecisionTree,
	type RehabDecisionTree,
	type RehabActionsResolvedResponse,
	type RehabAction,
	type AddRehabTreePayload,
	type RehabCostResolvedResponse,
	type RehabTreeStatusResolvedResponse,
	type RehabTreesAndIdsResponse,
	type AssetFilter,
	AGSetMode,
	TreeType,
} from '@Types/rehab.types';
import axios, { type AxiosError, type AxiosResponse } from 'axios';
import {
	getRehabTreesApi,
	getRehabTreeApi,
	addRehabTreeApi,
	updateRehabTreeApi,
	deleteRehabTreeApi,
	getRehabActionsApi,
	validateRehabTreeApi,
	runRehabTreeApi,
	getRehabResultApi,
	updateRehabStatus,
	duplicateRehabTreeApi,
	getRehabCostApi,
	renameRehabTreeApi,
	getRehabTreeStatusApi,
	getRehabTreeValidationStatusApi,
	getAssetFiltersApi,
} from '@Services/Rehab/Rehab.services';
import {
	convertTreeToReactFlowObject,
	convertReactFlowObjectToTree,
	getMainTreeNodesIds,
	extractDraftNodes,
	getStartNodeId,
	TREE_STATES,
} from '@Utils/TreeUtils';
import { type AnyAction } from 'redux';
import { success, error } from '@innovyze/stylovyze';
import {
	currentStartNodeIdSelector,
	CurrentTreeSelector,
	selectorNewTreeQuery,
	getRehabActionsSelector,
	getReactFlowTreeSelector,
	selectorRehabTreesPaginationQuery,
	getRehabDeletedActionsSelector,
	rehabResultQuerySelector,
} from '@Selectors/Rehab.selectors';
import { type ResolvedResponse, type Row } from '@innovyze/lib-am-common';
import { t } from 'i18next';
import { getNewAssetIds } from '@innovyze/lib_asset_schema';

export function* getRehabTreesSaga() {
	try {
		yield put(setRehabTreesIsBusy(true));

		let query: string = yield select(selectorRehabTreesPaginationQuery);

		query = query + (query.length === 0 ? '?' : '&') + 'treeType=AGSet';

		const payload: AxiosResponse<ResolvedResponse> = yield retry(
			5,
			15000,
			getRehabTreesApi,
			query,
		);

		// Normalize systemType and assetType to IDs
		const normalizedData = payload.data.data.map((tree: any) => {
			const { systemTypeId, assetTypeId } = getNewAssetIds({
				systemType: tree.systemType,
				assetType: tree.assetType,
			});
			return {
				...tree,
				systemType: systemTypeId,
				assetType: assetTypeId,
			};
		});

		yield put(
			getRehabTreesResolved({
				pagination: payload.data.pagination,
				data: normalizedData,
			}),
		);
		yield put(setRehabTreesIsBusy(false));
	} catch (e) {
		const err = e as AxiosError;
		const rejection = {
			code: err?.response?.status ?? -1,
			text:
				err?.response?.statusText ??
				t('No additional information') ??
				'',
		};

		yield put(getRehabTreesRejected(rejection));
		yield put(setRehabTreesIsBusy(false));
	}
}

function* watchGetRehabTrees() {
	yield takeLatest(
		[
			getRehabTrees,
			reloadRehabTrees,
			changeRehabTreesPage,
			changeRehabTreesSlice,
			changeRehabTreesSort,
		],
		getRehabTreesSaga,
	);
}

//use input action to check if need to load trees for default page
export function* getRehabTreesListSaga() {
	try {
		yield put(setRehabTreesIsBusy(true));

		const payload: AxiosResponse<RehabTreesAndIdsResponse> = yield retry(
			5,
			15000,
			getRehabTreesApi,
			'?listAllAndTrees=true&treeType=AGSet',
		);

		// Normalize systemType and assetType to IDs
		const normalizedData = payload.data.data.map((tree: any) => {
			const { systemTypeId, assetTypeId } = getNewAssetIds({
				systemType: tree.systemType,
				assetType: tree.assetType,
			});
			return {
				...tree,
				systemType: systemTypeId,
				assetType: assetTypeId,
			};
		});

		yield put(getRehabTreesListResolved(payload.data.allTreeIds));

		yield put(
			getRehabTreesResolved({
				pagination: payload.data.pagination,
				data: normalizedData,
			}),
		);

		yield put(setRehabTreesIsBusy(false));
	} catch (e) {
		const err = e as AxiosError;
		const rejection = {
			code: err?.response?.status ?? -1,
			text:
				err?.response?.statusText ??
				t('No additional information') ??
				'',
		};

		yield put(getRehabTreesListRejected(rejection));
		yield put(setRehabTreesIsBusy(false));
	}
}

function* watchGetRehabTreesList() {
	yield takeLatest([getRehabTreesList], getRehabTreesListSaga);
}

export function* getRehabTreeSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<RehabTreeResolvedResponse> = yield retry(
			5,
			1500,
			getRehabTreeApi,
			action.payload.Id,
		);
		let reactFlowDecisionTree: ReactFlowDecisionTree;

		if (payload.data.data) {
			const treeNodes: ReactFlowSchema[] = convertTreeToReactFlowObject(
				payload.data.data.treeNodes,
			);

			if (
				payload.data.data.draftNodes &&
				payload.data.data.draftNodes?.length > 0
			) {
				payload.data.data.draftNodes.forEach(draft => {
					const draftNode = draft;
					if (draftNode.data) {
						draftNode.data.nodeData.assetsCount = {};
					}
					if (draft.source && draft.label) {
						draftNode.label = draft.label?.split(' ')[0];
					}

					treeNodes.push(draftNode);
				});
				const startNodeId = getStartNodeId(
					payload.data.data.draftNodes,
				);

				yield put(setStartNodeId(startNodeId));
			}

			const startNodeCheck: any = getStartNodeId(treeNodes);

			//implement start node new feature for existing trees
			if (
				treeNodes.length > 0 &&
				payload.data.data.draftNodes &&
				payload.data.data.draftNodes.length === 0 &&
				!startNodeCheck
			) {
				const startNode = {
					id: 'idxStart_' + new Date().getTime().toString(),
					// Cant translate as the label is used for code :(
					data: { label: 'Start' },
					style: {
						border: '1px solid #5F60FF',
						padding: 10,
						background: '#5F60FF',
						borderRadius: '1rem',
						color: 'white',
						fontSize: '1.2rem',
					},
					type: 'input',
					position: { x: 700, y: 50 },
				};
				treeNodes.push(startNode as any);
				yield put(setStartNodeId(startNode.id));
			}

			const { systemTypeId, assetTypeId } = getNewAssetIds({
				systemType: payload.data.data.systemType,
				assetType: payload.data.data.assetType,
			});

			reactFlowDecisionTree = {
				_id: payload.data.data._id ?? '',
				name: payload.data.data.name,
				thumbnail: payload.data.data.thumbnail,
				nodes: treeNodes,
				status: payload.data.data.status,
				runBy: payload.data.data.runBy,
				lastRun: payload.data.data.lastRun,
				systemType: systemTypeId,
				assetType: assetTypeId,
				assetCount: payload.data.data.assetCount,
				nodeCount: payload.data.data.nodeCount,
				inspectionStandard:
					payload.data.data.inspectionStandard ?? 'PACP',
				description: payload.data.data.description,
				treeType: payload.data.data.treeType,
				mode: payload.data.data.mode,
				config: payload.data.data.config,
				groupCount: payload.data.data.groupCount,
				createdAt: payload.data.data.createdAt,
				createdBy: payload.data.data.createdBy,
				updatedAt: payload.data.data.updatedAt,
				updatedBy: payload.data.data.updatedBy,
			};

			// Use the new actions, if we dont have any it COULD be an old tree so we need to load them the old way.
			if (
				payload.data.data.actions &&
				payload.data.data.actions.length !== 0
			) {
				yield put(getRehabActionsResolved(payload.data.data.actions));
			} else {
				yield put(
					getRehabActions({
						Id: action.payload.Id,
					}),
				);
			}
			yield put(getRehabTreeResolved(reactFlowDecisionTree));

			if (
				payload.data.data.validationData &&
				payload.data.data.validationData.length > 0
			) {
				yield put(
					addRehabTreeValidationResult({
						treeId: payload.data.data._id ?? '',
						isValid: false,
						validationMessages: payload.data.data.validationData,
					}),
				);
			}

			yield put(setRehabTreePublishState(payload.data.data.publishState));

			// Calculate showWarning based on lastRun and updatedAt
			let showWarning = false;
			if (payload.data.data.lastRun && payload.data.data.updatedAt) {
				// Dates are different formats :(
				const run = new Date(
					payload.data.data.lastRun +
						(payload.data.data.lastRun.endsWith('Z') ? '' : 'Z'),
				);
				const save = new Date(payload.data.data.updatedAt);
				const diff = save.getTime() - run.getTime();
				// Give it a 60 second window
				showWarning = diff > 60000;
			}
			yield put(setShowWarning(showWarning));
			yield put(
				setShowReadyToUse(
					!showWarning &&
						payload.data.data.publishState !== 'published',
				),
			);
		}
	} catch (e) {
		yield put(getRehabTreeRejected());
	}
}

function* watchgetRehabTree() {
	yield takeLatest(getRehabTree, getRehabTreeSaga);
}

export function* addRehabTreeSaga(action: AnyAction) {
	try {
		const tree: NewDecisionTree = yield select(selectorNewTreeQuery);

		let nodes: any = null;
		let draftNodes: any = null;
		if (tree.createWithTreeNodes && tree.createWithTreeNodes.length > 0) {
			const startNodeId = getStartNodeId(tree.createWithTreeNodes);
			const mainTreeIds = getMainTreeNodesIds(
				tree.createWithTreeNodes,
				startNodeId,
			);

			const allTreeNodes = mainTreeIds
				? extractDraftNodes(
						tree.createWithTreeNodes,
						mainTreeIds as any,
					)
				: extractDraftNodes(tree.createWithTreeNodes, [] as any);

			let rootId = '';
			tree.createWithTreeNodes.forEach(node => {
				if (node.source && node.source === startNodeId && node.target) {
					rootId = node.target as string;
				}
			});
			nodes = convertReactFlowObjectToTree(
				allTreeNodes?.mainNodes,
				rootId,
			);
			draftNodes = allTreeNodes?.draftNodes;
		}

		const rehabTree: RehabDecisionTree = {
			_id: tree.createWithId ?? uuid.v4(),
			name: tree.name,
			systemType: tree.systemType,
			assetType: tree.assetType,
			assetFilterId: action.payload.assetFilterId
				? action.payload.assetFilterId
				: '',
			thumbnail: '',
			treeNodes: nodes ? nodes.treeNodes : null,
			draftNodes: draftNodes,
			inspectionStandard: tree.inspectionStandard,
			description: tree.description,
			mode: tree.mode,
			config: tree.config,
			groupCount: tree.groupCount,
			treeType: tree.treeType,
			actions: tree.actions,
		};
		const payload: AddRehabTreePayload = yield call(
			addRehabTreeApi,
			rehabTree,
		);
		yield put(addRehabTreeCreated());
		if (action.payload.runTree) {
			yield put(setInitalLoadSet(true));
			yield put(runRehabTree({ treeId: payload.data.treeId }));
		}

		if (action.payload.replaceHistory) {
			action.payload.history.replace(
				addRouteNamespace(`details/${payload.data.treeId}`),
			);
		} else {
			action.payload.history.push(
				addRouteNamespace(`details/${payload.data.treeId}`),
			);
		}
		yield put(setRefreshList(true));
		yield put(setIsDirty(false));
		yield put(clearScanId({}));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			if (e.response?.status == 400) {
				const errorMess =
					e?.response?.data?.errorMessage.replace('[400] ', '') ?? '';

				const errMessagesArr = JSON.parse(errorMess);
				yield all(
					errMessagesArr.message.map((errorMsg: string) =>
						put(error(errorMsg)),
					),
				);
			}
		} else {
			yield put(error(t('The process has failed')));
		}

		yield put(addRehabTreeRejected());
	}
}

function* watchAddRehabTreeSaga() {
	yield takeLatest(addRehabTree, addRehabTreeSaga);
}

export function* saveRehabTreeSaga(action: AnyAction) {
	try {
		const tree: NewDecisionTree = yield select(selectorNewTreeQuery);
		const actions: RehabAction[] = tree.actions
			? tree.actions
			: yield select(getRehabActionsSelector);
		const deletedActions: string[] = tree.deletedActions
			? tree.deletedActions
			: yield select(getRehabDeletedActionsSelector);
		const startNodeId: string = yield select(currentStartNodeIdSelector);
		const mainTreeIds = getMainTreeNodesIds(tree.treeNodes, startNodeId);

		const allTreeNodes = mainTreeIds
			? extractDraftNodes(tree.treeNodes, mainTreeIds as any)
			: extractDraftNodes(tree.treeNodes, [] as any);

		let nodes: any = null;
		if (allTreeNodes?.mainNodes.length > 0) {
			let rootId = '';
			tree.treeNodes?.forEach(node => {
				if (node.source && node.source === startNodeId && node.target) {
					rootId = node.target as string;
				}
			});
			nodes = convertReactFlowObjectToTree(
				allTreeNodes?.mainNodes,
				rootId,
			);
		}

		const rehabTree: RehabDecisionTree = {
			_id: action.payload.treeId,
			name: tree.name ?? '',
			thumbnail: '',
			treeNodes: nodes ? nodes.treeNodes : {},
			draftNodes: allTreeNodes?.draftNodes
				? allTreeNodes?.draftNodes
				: [],
			description: tree.description ?? '',
			mode: tree.mode ?? AGSetMode.CUSTOM,
			config: tree.config,
			groupCount: tree.groupCount,
			treeType: tree.treeType ?? TreeType.AGSet,
			actions: actions.length !== 0 ? actions : undefined,
			deletedActions:
				deletedActions.length !== 0 ? deletedActions : undefined,
		};

		yield call(updateRehabTreeApi, action.payload.treeId, rehabTree);
		yield put(
			getRehabTree({
				Id: action.payload.treeId,
			}),
		);
		yield put(addRehabTreeResolved());
		yield put(
			addRehabTreeValidationResult({
				treeId: action.payload.treeId,
				isValid: true,
				validationMessages: [],
			}),
		);
		if (allTreeNodes?.mainNodes.length > 0) {
			yield put(validateRehabTree({ treeId: action.payload.treeId }));
		} else {
			yield put(
				getRehabTree({
					Id: action.payload.treeId,
				}),
			);
		}
		yield put(
			success(
				t(
					'Tree has updated successfully, preview asset count is in progress',
				),
			),
		);

		if (action.payload.history) {
			if (action.payload.replaceHistory) {
				action.payload.history.replace(
					addRouteNamespace(`details/${action.payload.treeId}`),
				);
			} else {
				action.payload.history.push(
					addRouteNamespace(`details/${action.payload.treeId}`),
				);
			}
		}
		yield put(setRefreshList(true));
		yield put(setIsDirty(false));
		yield put(clearScanId({}));
	} catch (e: any) {
		if ((e as AxiosError) && e?.response?.status == 400) {
			const errorMess =
				e?.response?.data?.errorMessage.replace('[400] ', '') ?? '';

			const errMessagesArr = JSON.parse(errorMess);

			const validationMessages = errMessagesArr.message.map(
				(ele: string) => {
					return { errorMessage: ele };
				},
			);
			yield put(
				addRehabTreeValidationResult({
					treeId: action.payload.treeId,
					isValid: false,
					validationMessages: validationMessages,
				}),
			);
			yield put(error(t('Invalid tree')));
		} else {
			yield put(error(t('Update tree has failed')));
		}
		yield put(addRehabTreeRejected());
	}
}

function* watchSaveRehabTreeSaga() {
	yield takeLatest(saveRehabTree, saveRehabTreeSaga);
}

export function* deleteRehabTreeSaga(action: AnyAction) {
	try {
		yield call(deleteRehabTreeApi, action.payload.treeId);
		yield put(deleteRehabTreeResolved(action.payload.treeId));
		yield put(success(t('Asset Group Set deleted successfully')));
		action.payload.refresh();
	} catch (_e) {
		const e = _e as AxiosError;
		if (e) {
			if (e.response?.status === 400) {
				yield put(
					error(
						t(
							'Unable to delete a published Asset Group Set. Please un-publish the Asset Group Set prior to deletion.',
						),
					),
				);
			}
		}
		yield put(deleteRehabTreeRejected());
	}
}
function* watchDeleteRehabTreeSaga() {
	yield takeLatest(deleteRehabTree, deleteRehabTreeSaga);
}

export function* getRehabActionsSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<RehabActionsResolvedResponse> =
			yield retry(5, 1500, getRehabActionsApi, action.payload.Id);
		yield put(getRehabActionsResolved(payload.data.data));

		const rehabActions = payload.data.data;
		let options: {
			id: string;
			caption: string;
		}[] = []; //[AM_COMMON.noFilters()];
		if (rehabActions && rehabActions.length > 0) {
			options = [
				...rehabActions.map(action => {
					return { id: action._id, caption: action.actionId };
				}),
			];
		}
		yield put(
			updateColumnFilterSelectOptions({
				colKey: 'FINAL_RRHAB_ACTION',
				options: options,
			}),
		);
	} catch (e) {
		yield put(getRehabActionsRejected());
	}
}
function* watchGetRehabActions() {
	yield takeLatest(getRehabActions, getRehabActionsSaga);
}

export function* validateRehabTreeSaga(action: AnyAction) {
	try {
		yield call(validateRehabTreeApi, action.payload.treeId);
		yield put(getRehabTreeValidationStatus({ Id: action.payload.treeId }));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			let errorMess = e?.response?.data?.errorMessage ?? '';

			if (e.response?.status == 400 || errorMess.includes('400')) {
				errorMess = errorMess.replace('[400] ', '') ?? '';

				const errMessagesArr = JSON.parse(errorMess);

				yield put(error(t('Validation failed')));
				if (errMessagesArr && Array.isArray(errMessagesArr.message)) {
					yield put(
						addRehabTreeValidationResult({
							treeId: action.payload.treeId as string,
							isValid: false,
							validationMessages: [
								{ errorMessage: errMessagesArr.message },
							],
						}),
					);
				} else {
					yield put(
						addRehabTreeValidationResult({
							treeId: action.payload.treeId as string,
							isValid: false,
							validationMessages: [
								{ errorMessage: errMessagesArr.message },
							],
						}),
					);
				}
			}

			yield put(validateRehabTreeRejected());
		} else {
			yield put(error(t('Validation failed')));
			yield put(validateRehabTreeRejected());
		}
	}
}

function* watchValidateRehabTreeSaga() {
	yield takeLatest(validateRehabTree, validateRehabTreeSaga);
}

export function* runRehabTreeSaga(action: AnyAction) {
	try {
		const startNodeId: string = yield select(currentStartNodeIdSelector);
		const currentTree: ReactFlowDecisionTree = yield select(
			getReactFlowTreeSelector,
		);

		const mainTreeIds = getMainTreeNodesIds(currentTree.nodes, startNodeId);

		const allTreeNodes = mainTreeIds
			? extractDraftNodes(currentTree.nodes, mainTreeIds as any)
			: extractDraftNodes(currentTree.nodes, [] as any);

		if (allTreeNodes?.mainNodes.length > 0) {
			let rootId = '';
			currentTree.nodes.forEach(node => {
				if (node.source && node.source === startNodeId && node.target) {
					rootId = node.target as string;
				}
			});
			convertReactFlowObjectToTree(allTreeNodes?.mainNodes, rootId);
		}
		yield call(
			updateRehabStatus,
			action.payload.treeId,
			TREE_STATES.IN_PROGRESS,
		);
		yield call(runRehabTreeApi, action.payload.treeId);

		yield put(
			getRehabTree({
				Id: action.payload.treeId,
			}),
		);
		yield put(
			getRehabActions({
				Id: action.payload.treeId,
			}),
		);
		yield put(success(t('Tree is running')));
		yield put(getRehabTreeStatus({ Id: action.payload.treeId }));
	} catch (e) {
		const err = e as AxiosError;
		yield put(runRehabTreeRejected());

		if (axios.isAxiosError(e)) {
			if (e.response?.status == 400) {
				const errorMess =
					e?.response?.data?.errorMessage.replace('[400] ', '') ?? '';

				const errMessagesArr = JSON.parse(errorMess);

				const validationMessages = errMessagesArr.message.map(
					(ele: string) => {
						return { errorMessage: ele };
					},
				);
				yield put(
					addRehabTreeValidationResult({
						treeId: action.payload.treeId as string,
						isValid: false,
						validationMessages: validationMessages,
					}),
				);
				yield put(error(t('Invalid tree')));
			}
		}

		if (err.message.includes('timeout')) {
			yield put(success(t('Tree is running')));
			yield call(
				updateRehabStatus,
				action.payload.treeId,
				TREE_STATES.COMPLETE,
			);
		} else {
			yield put(error(t('Run failed')));
			yield call(
				updateRehabStatus,
				action.payload.treeId,
				TREE_STATES.FAILED,
			);
		}
	}
}
function* watchRunRehabTreeSaga() {
	yield takeLatest(runRehabTree, runRehabTreeSaga);
}

export function* getRehabResultSaga() {
	try {
		yield put(
			setRehabResultIsBusy({ forGroupResults: false, isBusy: true }),
		);

		const query: string = yield select(rehabResultQuerySelector(false));

		const treeId: string = yield select(CurrentTreeSelector);
		// quick tmp fix - will do via custom header eventually
		const payload: AxiosResponse<ResolvedResponse> = yield retry(
			5,
			1500,
			getRehabResultApi,
			treeId,
			query,
		);
		const rows = payload.data.data as Row[];

		const data = {
			...payload.data,
			data: rows.map(result => {
				if (result.ASSET_ID?.indexOf('#') > 0) {
					return {
						...result,
						ASSET_ID: result.ASSET_ID.split('#')[1],
					};
				} else {
					return result;
				}
			}),
		};
		yield put(getRehabResultResolved({ response: data }));
		yield put(
			setRehabResultIsBusy({ forGroupResults: false, isBusy: false }),
		);
	} catch (e) {
		const { response }: any = e;
		yield put(
			getRehabResultRejected({
				status: {
					code: response?.status ?? -1,
					text:
						response?.statusText ?? t('No additional information'),
				},
			}),
		);
		yield put(
			setRehabResultIsBusy({ forGroupResults: false, isBusy: false }),
		);
	}
}

function* watchGetRehabResult() {
	yield takeLatest(
		[
			getRehabResult,
			reloadRehabResult,
			changePage,
			changePagination,
			changeSort,
			changeFilterSelect,
			changeFiltersSelect,
		],
		getRehabResultSaga,
	);
}

export function* getGroupResultSaga() {
	try {
		yield put(
			setRehabResultIsBusy({ forGroupResults: true, isBusy: true }),
		);

		let query: string = yield select(rehabResultQuerySelector(true));

		query = query + (query.length === 0 ? '?' : '&') + 'treeType=AGSet';

		const treeId: string = yield select(CurrentTreeSelector);
		// quick tmp fix - will do via custom header eventually
		const payload: AxiosResponse<ResolvedResponse> = yield retry(
			5,
			1500,
			getRehabResultApi,
			treeId,
			query,
		);
		const rows = payload.data.data as Row[];

		const data = {
			...payload.data,
			data: rows.map(result => {
				if (result.ASSET_ID?.indexOf('#') > 0) {
					return {
						...result,
						ASSET_ID: result.ASSET_ID.split('#')[1],
					};
				} else {
					return result;
				}
			}),
		};
		yield put(getGroupResultResolved({ response: data }));
		yield put(
			setRehabResultIsBusy({ forGroupResults: true, isBusy: false }),
		);
	} catch (e) {
		const { response }: any = e;
		yield put(
			getRehabResultRejected({
				status: {
					code: response?.status ?? -1,
					text:
						response?.statusText ?? t('No additional information'),
				},
			}),
		);
		yield put(
			setRehabResultIsBusy({ forGroupResults: true, isBusy: false }),
		);
	}
}

function* watchGetGroupResult() {
	yield takeLatest([getGroupResult, reloadGroupResult], getGroupResultSaga);
}

export function* duplicateRehabTreeSaga(action: AnyAction) {
	try {
		const response: AxiosResponse = yield call(
			duplicateRehabTreeApi,
			action.payload.treeId,
		);
		yield put(duplicateRehabTreeResolved());
		yield put(setRefreshList(true));
		yield put(setIsDirty(false));
		yield put(clearScanId({}));
		action.payload.onComplete(response.data.treeId);
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			// const data = axiosError.response?.data as ConfigData;
			yield put(
				error(
					t('Failed to create rehab tree: {{info}}', {
						info:
							axiosError.response?.statusText ??
							// data?.errorMessage ??
							t('No additional information'),
					}),
				),
			);
		} else {
			yield put(
				error(
					t('Failed to create rehab tree: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(duplicateRehabTreeRejected());
	}
}
function* watchDuplicateRehabTreeSaga() {
	yield takeLatest(duplicateRehabTree, duplicateRehabTreeSaga);
}
interface RenameData {
	errorMessage: string;
}

export function* renameRehabTreeSaga(action: AnyAction) {
	const { treeId, name, description } = action.payload;
	try {
		yield put(setRehabTreesIsRenaming(true));
		yield call(renameRehabTreeApi, treeId, name, description);
		yield put(success(t('Tree renamed')));
		yield put(setRehabTreesIsRenaming(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as RenameData;
			if (data?.errorMessage === '[400] Duplicate tree name') {
				yield put(setRehabTreeNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Tree failed to save: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Tree failed to save: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRehabTreesIsRenaming(false));
	}
}

function* watchRenameRehabTree() {
	yield takeLatest(renameRehabTree, renameRehabTreeSaga);
}

export function* getRehabCostSaga(action: AnyAction) {
	try {
		// const treeId: string = yield select(CurrentTreeSelector);

		// quick tmp fix - will do via custom header eventually
		const payload: AxiosResponse<RehabCostResolvedResponse> = yield retry(
			5,
			1500,
			getRehabCostApi,
			action.payload,
		);
		yield put(getRehabCostResolved(payload.data.data));
	} catch (e) {
		const { response }: any = e;
		yield put(
			getRehabCostRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
	}
}

function* watchGetRehabCostSaga() {
	yield takeLatest(getRehabCost, getRehabCostSaga);
}

export function* getRehabTreeStatusSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<RehabTreeStatusResolvedResponse> =
			yield retry(1, 1500, getRehabTreeStatusApi, action.payload.Id);
		yield put(getRehabTreeStatusResolved(payload.data.status));
	} catch (err) {
		yield put(getRehabTreeStatusRejected());
	}
}

function* watchGetRehabTreeStatusSaga() {
	yield takeLatest(getRehabTreeStatus, getRehabTreeStatusSaga);
}

export function* getRehabTreeValidationStatusSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<RehabTreeStatusResolvedResponse> =
			yield retry(
				1,
				1500,
				getRehabTreeValidationStatusApi,
				action.payload.Id,
			);
		yield put(getRehabTreeValidationStatusResolved(payload.data.status));
		if (payload.data.status == 'Failed') {
			yield put(getRehabTree({ Id: action.payload.Id }));
		}
	} catch (err) {
		yield put(getRehabTreeValidationStatusRejected());
	}
}

function* watchGetRehabTreeValidationStatusSaga() {
	yield takeLatest(
		getRehabTreeValidationStatus,
		getRehabTreeValidationStatusSaga,
	);
}

export function* getAssetFiltersSaga() {
	try {
		const payload: AxiosResponse<AssetFilter[]> = yield retry(
			5,
			1500,
			getAssetFiltersApi,
		);

		yield put(getAssetFiltersResolved(payload.data));
	} catch (e) {
		yield put(getAssetFiltersRejected());
	}
}

function* watchGetAssetFilters() {
	yield takeLatest(getAssetFilters, getAssetFiltersSaga);
}

const sagaArray = [
	watchGetRehabTrees(),
	watchGetRehabTreesList(),
	watchgetRehabTree(),
	watchAddRehabTreeSaga(),
	watchSaveRehabTreeSaga(),
	watchDeleteRehabTreeSaga(),
	watchGetRehabActions(),
	watchValidateRehabTreeSaga(),
	watchGetRehabResult(),
	watchRunRehabTreeSaga(),
	watchDuplicateRehabTreeSaga(),
	watchGetRehabCostSaga(),
	watchRenameRehabTree(),
	watchGetRehabTreeStatusSaga(),
	watchGetRehabTreeValidationStatusSaga(),
	watchGetAssetFilters(),
	watchGetGroupResult(),
];

export default sagaArray;
